import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog } from "@/components/ui/dialog";

export interface ConfirmOptions {
    title: string;
    message?: ReactNode;
    confirmLabel?: string;
    cancelLabel?: string;
    destructive?: boolean;
    icon?: ReactNode;
}

export interface PromptOptions {
    title: string;
    label?: string;
    description?: ReactNode;
    defaultValue?: string;
    placeholder?: string;
    confirmLabel?: string;
    /** Pre-select up to this many characters (e.g. the name without its extension). */
    selectLength?: number;
    /** Return an error message to block submit. */
    validate?: (value: string) => string | null;
    icon?: ReactNode;
}

interface DialogsApi {
    confirm: (opts: ConfirmOptions) => Promise<boolean>;
    prompt: (opts: PromptOptions) => Promise<string | null>;
}

const DialogsContext = createContext<DialogsApi | null>(null);

export function useDialogs(): DialogsApi {
    const ctx = useContext(DialogsContext);
    if (!ctx) throw new Error("useDialogs must be used inside <DialogsProvider>");
    return ctx;
}

type Pending =
    | { type: "confirm"; opts: ConfirmOptions; resolve: (v: boolean) => void }
    | { type: "prompt"; opts: PromptOptions; resolve: (v: string | null) => void };

/**
 * Promise-based replacements for window.confirm / window.prompt, rendered
 * as proper accessible dialogs.
 */
export function DialogsProvider({ children }: { children: ReactNode }) {
    const [pending, setPending] = useState<Pending | null>(null);
    const pendingRef = useRef<Pending | null>(null);
    pendingRef.current = pending;

    const cancelCurrent = () => {
        const p = pendingRef.current;
        if (!p) return;
        if (p.type === "confirm") p.resolve(false);
        else p.resolve(null);
    };

    const confirm = useCallback((opts: ConfirmOptions) => new Promise<boolean>((resolve) => {
        cancelCurrent();
        setPending({ type: "confirm", opts, resolve });
    }), []);

    const prompt = useCallback((opts: PromptOptions) => new Promise<string | null>((resolve) => {
        cancelCurrent();
        setPending({ type: "prompt", opts, resolve });
    }), []);

    const api = useMemo(() => ({ confirm, prompt }), [confirm, prompt]);

    const close = (value: boolean | string | null) => {
        const p = pendingRef.current;
        setPending(null);
        if (!p) return;
        if (p.type === "confirm") p.resolve(Boolean(value));
        else p.resolve(typeof value === "string" ? value : null);
    };

    return (
        <DialogsContext.Provider value={api}>
            {children}
            {pending?.type === "confirm" ? (
                <Dialog
                    open
                    onClose={() => close(false)}
                    title={pending.opts.title}
                    icon={pending.opts.icon}
                    size="sm"
                    footer={
                        <>
                            <Button variant="ghost" onClick={() => close(false)}>{pending.opts.cancelLabel ?? "Cancel"}</Button>
                            <Button
                                variant={pending.opts.destructive ? "destructive" : "default"}
                                onClick={() => close(true)}
                                data-autofocus=""
                            >
                                {pending.opts.confirmLabel ?? "OK"}
                            </Button>
                        </>
                    }
                >
                    {pending.opts.message ? <div className="text-sm text-muted-foreground">{pending.opts.message}</div> : null}
                </Dialog>
            ) : null}
            {pending?.type === "prompt" ? (
                <PromptDialog key={pending.opts.title + (pending.opts.defaultValue ?? "")} opts={pending.opts} onDone={close} />
            ) : null}
        </DialogsContext.Provider>
    );
}

function PromptDialog({ opts, onDone }: { opts: PromptOptions; onDone: (v: string | null) => void }) {
    const [value, setValue] = useState(opts.defaultValue ?? "");
    const [error, setError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const el = inputRef.current;
        if (!el) return;
        el.focus();
        const len = opts.selectLength ?? el.value.length;
        el.setSelectionRange(0, len);
    }, [opts.selectLength]);

    const submit = () => {
        const v = value.trim();
        if (!v) {
            setError("Please enter a name.");
            return;
        }
        const custom = opts.validate?.(v) ?? null;
        if (custom) {
            setError(custom);
            return;
        }
        onDone(v);
    };

    return (
        <Dialog
            open
            onClose={() => onDone(null)}
            title={opts.title}
            description={opts.description}
            icon={opts.icon}
            size="sm"
            footer={
                <>
                    <Button variant="ghost" onClick={() => onDone(null)}>Cancel</Button>
                    <Button onClick={submit}>{opts.confirmLabel ?? "OK"}</Button>
                </>
            }
        >
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    submit();
                }}
            >
                {opts.label ? <label htmlFor="drive-prompt-input" className="mb-1 block text-xs font-medium text-muted-foreground">{opts.label}</label> : null}
                <Input
                    id="drive-prompt-input"
                    ref={inputRef}
                    data-autofocus=""
                    value={value}
                    placeholder={opts.placeholder}
                    onChange={(e) => {
                        setValue(e.target.value);
                        setError(null);
                    }}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={error ? "drive-prompt-error" : undefined}
                    className="h-10"
                />
                {error ? <p id="drive-prompt-error" className="mt-1.5 text-xs text-destructive">{error}</p> : null}
            </form>
        </Dialog>
    );
}
